import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "react-toastify";
import { errorLogger } from "@/utils/helper";
import {
  useGetAllMerchant,
  useInviteMerchantUser,
} from "./mutations";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect } from "react";

const formSchema = z.object({
  name: z.string().min(1, "Merchant User Name is required"),
  email: z.string().email("Invalid email address"),
  merchantId: z.string().min(1, "Merchant ID is required"),
  merchantName: z.string().min(1, "Merchant Name is required"),
});

export type MerchantDTO = z.infer<typeof formSchema>;

type MerchantFormProps = {
  onSubmit: (data: MerchantDTO) => void;
  onClose: () => void;
  isPending: boolean;
};

function MerchantUserForm({ onSubmit, onClose, isPending }: MerchantFormProps) {
  const form = useForm<MerchantDTO>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      merchantId: "",
      merchantName: "",
    },
  });

  const { data: merchants } = useGetAllMerchant({
    size: 1000000,
    page: 1,
  });

  useEffect(() => {
    form.setValue(
      "merchantId",
      merchants?.merchants.find(
        (v) => v.merchantName === form.watch("merchantName")
      )?.merchantId || ""
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.watch("merchantName")]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 grow">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Merchant User Name</FormLabel>
              <FormControl>
                <Input {...field} className="h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Merchant User Email</FormLabel>
              <FormControl>
                <Input type="email" {...field} className="h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="merchantName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Merchant Name</FormLabel>
              <FormControl>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={`${field.value}`}
                >
                  <FormControl>
                    <SelectTrigger className="w-full px-4">
                      <SelectValue placeholder="Select Merchant" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {merchants?.merchants.map((v, key) => (
                      <SelectItem key={key} value={v.name ?? v.merchantName}>
                        {v.name ?? v.merchantName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={isPending} isLoading={isPending} className="bg-primary" type="submit">
            Invite
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

type MerchantFormModalProps = {
  isOpen?: boolean;
  onClose: () => void;
};

function MerchantUserFormModal({ isOpen, onClose }: MerchantFormModalProps) {
  const { mutateAsync: inviteMerchant, isPending: isPendingInvite } =
    useInviteMerchantUser();

  async function handleSubmit(data: MerchantDTO) {
    try {
      const userData = {
        ...data,
        email: data.email.toLowerCase(),
      };

      await inviteMerchant(userData, {
        onSuccess() {
          toast.success("Merchant User Successfully Invited");
          onClose();
        },
      });
    } catch (error) {
      errorLogger(error);
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="h-[65%] overflow-y-auto">
        <DialogHeader className="flex h-fit">
          <DialogTitle>Invite Merchant User</DialogTitle>
        </DialogHeader>

        <MerchantUserForm
          onSubmit={handleSubmit}
          onClose={onClose}
          isPending={isPendingInvite}
        />
      </DialogContent>
    </Dialog>
  );
}

export default MerchantUserFormModal;
